import React, { useEffect, useRef } from 'react'

import { AlignmentProps, ButtonTypes, LinkTypes } from '@cox/core-ui8/dist/Button'
import './ReactStoreLocatorList.scss'
import parse from 'html-react-parser'

import { COX_MOBILE_LOGO } from '../../../assets/images'
import { getAddressLine2, getStoreStatus } from '../../../utils/inversion-util'
import ReactButton from '../../ReactButton'
import ReactImage from '../../ReactImage'
import ReactText from '../../ReactText'
import { ReactListProps } from '../types'

// Memoized list item to prevent re-renders when only hover state changes
interface StoreListItemProps {
  item: any
  index: number
  storeId: string
  currentTime: Date
  onStoreHover?: (storeId: string | null) => void
  onStoreSelect?: (storeId: string | null) => void
  setRef: (storeId: string, el: HTMLDivElement | null) => void
}

const StoreListItem = React.memo(
  (props: StoreListItemProps) => {
    const { item, index, storeId, currentTime, onStoreHover, onStoreSelect, setRef } = props
    const bottomImage = COX_MOBILE_LOGO

    const {
      name,
      address_line1,
      address_line2,
      address_city,
      address_state,
      address_zip,
      store_phonenumber,
      store_tags,
      directions_link,
      slug
    } = item

    const addressText = (
      <div className='storelocator-list__address'>
        <div>
          {address_line1}, {address_line2 !== 'NULL' ? `${getAddressLine2(address_line2)},` : ''} <br />
          {address_city}, {address_state} {address_zip}{' '}
        </div>
        <div className='storelocator-list__directions-status'>
          <ReactText text={`${getStoreStatus(item, currentTime)}`} isParsed={false} />
        </div>
        <div className='storelocator-list__phone-number'>{store_phonenumber}</div>
      </div>
    )

    const websiteUrl = encodeURI(
      `/residential/local/stores/${address_state.toLowerCase()}/${address_city.toLowerCase().split(' ').join('-')}/${slug}.html`
    )

    return (
      <div
        className={`storelocator-list__card`}
        ref={(el) => setRef(storeId, el)}
        onMouseEnter={() => onStoreHover?.(storeId)}
        onMouseLeave={() => onStoreHover?.(null)}
        onClick={() => onStoreSelect?.(storeId)}
      >
        <div className='storelocator-list__card-content'>
          <div className='storelocator-list__card-content-storename'>{`${name}: ${address_city}`}</div>
          <div className='storelocator-list__card-content-address'>{addressText}</div>
          <div className='storelocator-list__card-content-mobile'>
            {store_tags?.toLowerCase() !== 'null' && store_tags != null && (
              <div className='storelocator-list__card-content-mobile-logo'>
                {store_tags.length > 0 && <ReactImage src={bottomImage} alt={'Cox Mobile Logo'} />}
              </div>
            )}
          </div>
        </div>
        <div className='storelocator-list__card-buttons'>
          {slug && slug?.length > 0 && (
            <ReactButton
              text={'Website'}
              buttonTypes={ButtonTypes.PRIMARY}
              linkType={LinkTypes.PAGE}
              alignment={AlignmentProps.LEFT}
              linkUrl={websiteUrl}
              index={index}
            />
          )}
          {directions_link &&
            directions_link?.length > 0 &&
            directions_link?.toLowerCase() !== 'null' &&
            directions_link != null && (
              <div className='link-section'>
                <div>
                  <ReactButton
                    text={'Directions'}
                    buttonTypes={ButtonTypes.TERTIARY}
                    linkUrl={directions_link}
                    openInNewTab={true}
                  />
                </div>
              </div>
            )}
        </div>
      </div>
    )
  },
  // Custom comparison: only re-render if item data changes, not when hover/select state changes
  (prevProps, nextProps) => {
    return (
      prevProps.item === nextProps.item && prevProps.index === nextProps.index && prevProps.storeId === nextProps.storeId
    )
  }
)

StoreListItem.displayName = 'StoreListItem'

const ReactStoreLocatorList = React.memo((props: ReactListProps): JSX.Element => {
  const {
    storeText = '',
    items,
    hoveredStoreId,
    selectedStoreId,
    onStoreHover,
    onStoreSelect,
    hasSearched,
    noStoresMessage,
    noStoresSubheading
  } = props
  const storeCount = items?.length
  const isNoResults = storeCount === 0
  const currentTime = new Date()

  // Use refs to track DOM elements for better performance
  const listItemRefs = useRef<Map<string, HTMLDivElement>>(new Map())

  // Stable callback for setting refs
  const setRef = useRef((storeId: string, el: HTMLDivElement | null) => {
    if (el) {
      listItemRefs.current.set(storeId, el)
    } else {
      listItemRefs.current.delete(storeId)
    }
  }).current

  // Update visual state without causing re-renders
  useEffect(() => {
    listItemRefs.current.forEach((element, storeId) => {
      // Remove all state classes first
      element.classList.remove('storelocator-list__card--hovered', 'storelocator-list__card--selected')

      // Add appropriate state class
      if (hoveredStoreId === storeId) {
        element.classList.add('storelocator-list__card--hovered')
      } else if (selectedStoreId === storeId) {
        element.classList.add('storelocator-list__card--selected')
      }
    })
  }, [hoveredStoreId, selectedStoreId])

  // Scroll to selected store when it changes
  useEffect(() => {
    if (selectedStoreId && listItemRefs.current.has(selectedStoreId)) {
      const element = listItemRefs.current.get(selectedStoreId)
      if (element) {
        // Find the scrollable parent container
        const scrollContainer = element.closest('.store-locator__store-list-container')

        if (scrollContainer) {
          // Calculate the element's position relative to the container
          const elementTop = element.offsetTop
          const containerScrollTop = scrollContainer.scrollTop
          const containerHeight = scrollContainer.clientHeight
          const elementHeight = element.clientHeight

          // Check if element is already fully visible
          const isAboveView = elementTop < containerScrollTop
          const isBelowView = elementTop + elementHeight > containerScrollTop + containerHeight

          // Only scroll if element is not fully visible
          if (isAboveView || isBelowView) {
            // Scroll to center the element in the container
            const targetScrollTop = elementTop - containerHeight / 2 + elementHeight / 2

            scrollContainer.scrollTo({
              top: Math.max(0, targetScrollTop),
              behavior: 'smooth'
            })
          }
        } else {
          // Fallback to scrollIntoView if container not found
          element.scrollIntoView({
            behavior: 'smooth',
            block: 'center',
            inline: 'nearest'
          })
        }
      }
    }
  }, [selectedStoreId])

  // Don't render anything if no search has been performed yet
  if (!hasSearched) {
    return <></>
  }

  return (
    <>
      <div className={`storelocator-list`}>
        <div className='storelocator-list__heading'>
          <div className='storelocator-list__headline'>{`${storeCount} ${storeText}`}</div>
          {isNoResults && noStoresSubheading && <div className='storelocator-list__subhead'>{noStoresSubheading}</div>}
        </div>
        {isNoResults && noStoresMessage ? (
          <div className='storelocator-list__no-results-message'>{parse(noStoresMessage)}</div>
        ) : (
          <div className='storelocator-list__cards'>
            {items?.map((item: any, index: any) => {
              const storeId = item.id || item.store_id || item.name
              return (
                <StoreListItem
                  key={storeId}
                  item={item}
                  index={index}
                  storeId={storeId}
                  currentTime={currentTime}
                  onStoreHover={onStoreHover}
                  onStoreSelect={onStoreSelect}
                  setRef={setRef}
                />
              )
            })}
          </div>
        )}
      </div>
    </>
  )
})

export default ReactStoreLocatorList
